// LollipopBenchmark — a normalized ratio-vs-benchmark lollipop chart for the
// Ratios hero. Each row plots the company's current value against a shared
// benchmark guide where 1.0x means "at benchmark". Lower-is-better ratios are
// inverted before they reach this component, so healthy values still land to the
// right of the guide line.
//
// Rendered as an HTML grid (not one big SVG) so the table reads as true columns
// — metric | source | benchmark | chart | company value — with a sticky-aligned
// header, a scrollable body window, and a per-row hide affordance.

import type { KeyboardEvent, MouseEvent } from 'react'

export type LollipopItem = {
  id?: string
  groupLabel?: string
  label: string
  /** Normalized score, where 1.0 is at benchmark. NaN = no benchmark to plot against;
   *  the row renders label + company value only (no dot/connector). */
  value: number
  /** Drives the dot color. */
  assessment?: 'healthy' | 'watch' | 'concern' | 'neutral'
  /** Pre-formatted value label (e.g. "1.82x"). */
  display?: string
  /** Pre-formatted benchmark label (e.g. "1.50x"). */
  benchmarkDisplay?: string
  companyDisplay?: string
  /** Where the benchmark comes from: IRS industry table, the company's own
   *  trailing average, or the ratio's healthy band. Styles the source pill
   *  and the dot (history rows render a ring instead of a solid dot). */
  benchmarkSource?: 'industry' | 'history' | 'band'
  /** Pill text in the source column (e.g. "IRS", "9-mo avg", "band"). */
  benchmarkTag?: string
  /** Why an unbenchmarked row has no dot (e.g. "no prior months yet"). */
  noBenchmarkNote?: string
}

export type LollipopBenchmarkProps = {
  items: LollipopItem[]
  ariaLabel: string
  rowHeight?: number
  /** Rows visible before the body scrolls (the header and axis legend stay put). */
  maxVisibleRows?: number
  className?: string
  selectedId?: string | null
  onItemClick?: (item: LollipopItem) => void
  /** When provided, each row gets a hover "hide" affordance. */
  onItemHide?: (item: LollipopItem) => void
}

const assessmentColor = (a: LollipopItem['assessment']): string => {
  switch (a) {
    case 'healthy':
      return 'var(--good-acc, #1c8d97)'
    case 'concern':
      return 'var(--neg, #c0492f)'
    case 'watch':
      return 'var(--check-acc, #b7791f)'
    default:
      return 'var(--anchor, #9aa0a8)'
  }
}

export type LollipopScale = { min: number; max: number }

/**
 * Display bounds for normalized scores. 1.0 = at benchmark; a score past 4x
 * (or below -1x) is either a data artifact or so far off-benchmark that exact
 * position adds nothing — pin it to the edge instead of letting one outlier
 * flatten every other row onto the guide line.
 */
export const LOLLIPOP_DISPLAY_MIN = -1
export const LOLLIPOP_DISPLAY_MAX = 4

export const clampLollipopValue = (value: number): number =>
  Math.min(LOLLIPOP_DISPLAY_MAX, Math.max(LOLLIPOP_DISPLAY_MIN, value))

/** Compute a shared x-scale across all normalized rows and the 1.0x guide. */
export function computeLollipopScale(items: LollipopItem[]): LollipopScale {
  if (!items.length) return { min: 0, max: 1 }
  const vals: number[] = []
  for (const it of items) {
    if (Number.isFinite(it.value)) vals.push(clampLollipopValue(it.value))
  }
  vals.push(0, 1)
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const pad = (max - min || Math.abs(max) || 1) * 0.12
  return { min: Math.min(0, min - pad), max: max + pad }
}

// Grid template shared by the header, every body row, and the axis-legend footer
// so the five columns stay perfectly aligned across the scroll boundary.
const GRID_COLUMNS = 'minmax(170px, 230px) 84px 106px minmax(220px, 1fr) 104px 30px'

const LOLLIPOP_CSS = `
  .fg-lolli { min-width: 760px; font-family: var(--font-ui); }
  .fg-lolli-grid { display: grid; grid-template-columns: ${GRID_COLUMNS}; column-gap: 12px; align-items: center; }
  .fg-lolli-head { padding: 4px 0 8px; border-bottom: 1px solid var(--grid, #ece8de); }
  .fg-lolli-head span { font: 700 12px var(--font-ui); color: var(--ink-2, #4c5360); }
  .fg-lolli-body { overflow-y: auto; overscroll-behavior: contain; }
  .fg-lolli-group { font: 800 11px var(--font-ui); color: var(--muted, #868c9a); text-transform: uppercase; letter-spacing: 0.06em; padding: 14px 0 4px; }
  .fg-lolli-row { position: relative; border-radius: 9px; cursor: default; outline: none; }
  .fg-lolli-row.is-interactive { cursor: pointer; }
  .fg-lolli-row::before { content: ''; position: absolute; inset: 2px 0; border-radius: 9px; background: var(--brand, #176b87); opacity: 0; transition: opacity 120ms ease; pointer-events: none; }
  .fg-lolli-row:hover::before, .fg-lolli-row:focus-visible::before { opacity: .07; }
  .fg-lolli-row[data-selected="true"]::before { opacity: .12; }
  .fg-lolli-row[data-selected="true"]:hover::before { opacity: .16; }
  .fg-lolli-label { font: 600 13.5px var(--font-ui); color: var(--ink, #1c1f26); line-height: 1.25; padding: 6px 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .fg-lolli-pill { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    font: 700 9.5px var(--font-ui); padding: 3px 8px; border-radius: 8px; color: var(--muted, #868c9a);
    border: 1.5px solid var(--grid, #ece8de); }
  .fg-lolli-pill.is-history { border: none; padding: 4.5px 9.5px; color: var(--brand, #176b87); background: color-mix(in srgb, var(--brand, #176b87) 13%, transparent); }
  .fg-lolli-bench { font: 700 13px var(--font-ui); color: var(--ink-2, #4c5360); text-align: right; white-space: nowrap; }
  .fg-lolli-note { font: italic 600 10.5px var(--font-ui); color: var(--muted, #868c9a); grid-column: 2 / 4; }
  .fg-lolli-track { position: relative; align-self: stretch; min-height: 100%; }
  .fg-lolli-track-line { position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px; background: var(--grid, #ece8de); }
  .fg-lolli-guide { position: absolute; top: 0; bottom: 0; width: 0; border-left: 1.5px dashed var(--muted, #868c9a); }
  .fg-lolli-connector { position: absolute; top: 50%; height: 4px; margin-top: -2px; border-radius: 2px; opacity: .5; }
  .fg-lolli-dot { position: absolute; top: 50%; width: 13px; height: 13px; border-radius: 50%; transform: translate(-50%, -50%); box-shadow: 0 0 0 3px var(--surface, #fffdf8); }
  .fg-lolli-dot.is-ring { width: 11px; height: 11px; background: var(--surface, #fffdf8); border-width: 3px; border-style: solid; }
  .fg-lolli-chevron { position: absolute; top: 50%; transform: translateY(-54%); font: 800 14px var(--font-ui); line-height: 1; }
  .fg-lolli-company { font: 800 14px var(--font-ui); text-align: center; white-space: nowrap; }
  .fg-lolli-hide { border: 0; background: transparent; color: var(--muted, #868c9a); font: 700 13px var(--font-ui);
    width: 24px; height: 24px; border-radius: 6px; cursor: pointer; opacity: 0; transition: opacity 120ms ease; padding: 0; }
  .fg-lolli-row:hover .fg-lolli-hide, .fg-lolli-row:focus-within .fg-lolli-hide, .fg-lolli-hide:focus-visible { opacity: 1; }
  .fg-lolli-hide:hover { background: var(--wash, #f3efe6); color: var(--ink-2, #4c5360); }
  .fg-lolli-foot { padding: 8px 0 2px; border-top: 1px solid var(--grid, #ece8de); }
  .fg-lolli-axis { position: relative; height: 16px; font: 600 11.5px var(--font-ui); color: var(--muted, #868c9a); }
  .fg-lolli-axis > span { position: absolute; top: 0; white-space: nowrap; }
`

export function LollipopBenchmark({
  items,
  ariaLabel,
  rowHeight = 46,
  maxVisibleRows = 10,
  className,
  selectedId = null,
  onItemClick,
  onItemHide,
}: LollipopBenchmarkProps) {
  const scale = computeLollipopScale(items)
  const span = scale.max - scale.min || 1
  const toPct = (v: number) => ((clampLollipopValue(v) - scale.min) / span) * 100
  const guidePct = ((1 - scale.min) / span) * 100
  const interactive = Boolean(onItemClick)
  const scrolls = items.length > maxVisibleRows
  const bodyMaxHeight = scrolls ? maxVisibleRows * rowHeight + rowHeight / 2 : undefined

  return (
    <div className={`fg-lolli${className ? ` ${className}` : ''}`} role="group" aria-label={ariaLabel}>
      <style>{LOLLIPOP_CSS}</style>
      <div className="fg-lolli-grid fg-lolli-head">
        <span>Metric</span>
        <span>Source</span>
        <span style={{ textAlign: 'right' }}>Benchmark</span>
        <span aria-hidden="true" />
        <span style={{ textAlign: 'center' }}>Company value</span>
        <span aria-hidden="true" />
      </div>
      <div
        className="fg-lolli-body"
        style={bodyMaxHeight ? { maxHeight: bodyMaxHeight } : undefined}
      >
        {items.map((it, i) => {
          const hasScore = Number.isFinite(it.value)
          const dotPct = hasScore ? toPct(it.value) : guidePct
          const clampedHigh = hasScore && it.value > LOLLIPOP_DISPLAY_MAX
          const clampedLow = hasScore && it.value < LOLLIPOP_DISPLAY_MIN
          const color = assessmentColor(it.assessment)
          const selected = Boolean(it.id && selectedId === it.id)
          const connLeft = Math.min(guidePct, dotPct)
          const connWidth = Math.abs(dotPct - guidePct)
          const tooltip = hasScore
            ? `${it.label}: ${it.companyDisplay || it.display || it.value.toFixed(2)}${it.benchmarkDisplay ? `; benchmark ${it.benchmarkDisplay}${it.benchmarkTag ? ` (${it.benchmarkTag})` : ''}` : ''}${clampedHigh || clampedLow ? ' (beyond chart scale)' : ''}`
            : `${it.label}: ${it.companyDisplay || it.display || '—'}; ${it.noBenchmarkNote || 'no benchmark yet'}`
          const handleHide = onItemHide
            ? (event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                onItemHide(it)
              }
            : undefined
          return (
            <div key={`${it.id || it.label}-${i}`}>
              {it.groupLabel ? <div className="fg-lolli-group">{it.groupLabel}</div> : null}
              <div
                className={`fg-lolli-grid fg-lolli-row${interactive ? ' is-interactive' : ''}`}
                style={{ minHeight: rowHeight }}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-label={interactive ? `Show ${it.label}` : undefined}
                aria-pressed={interactive ? selected : undefined}
                data-selected={selected ? 'true' : 'false'}
                title={tooltip}
                onClick={interactive ? () => onItemClick?.(it) : undefined}
                onKeyDown={
                  interactive
                    ? (event: KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onItemClick?.(it)
                        }
                      }
                    : undefined
                }
              >
                <span className="fg-lolli-label">{it.label}</span>
                {hasScore && it.benchmarkDisplay ? (
                  <>
                    <span>
                      {it.benchmarkTag ? (
                        <span className={`fg-lolli-pill${it.benchmarkSource === 'history' ? ' is-history' : ''}`}>
                          {it.benchmarkTag}
                        </span>
                      ) : null}
                    </span>
                    <span className="fg-lolli-bench">{it.benchmarkDisplay}</span>
                  </>
                ) : (
                  <span className="fg-lolli-note">{it.noBenchmarkNote || 'no benchmark yet'}</span>
                )}
                <span className="fg-lolli-track" aria-hidden="true">
                  <span className="fg-lolli-track-line" />
                  <span className="fg-lolli-guide" style={{ left: `${guidePct}%` }} />
                  {hasScore ? (
                    <>
                      {connWidth > 0.2 ? (
                        <span
                          className="fg-lolli-connector"
                          style={{ left: `${connLeft}%`, width: `${connWidth}%`, background: color }}
                        />
                      ) : null}
                      <span
                        className={`fg-lolli-dot${it.benchmarkSource === 'history' ? ' is-ring' : ''}`}
                        style={{
                          left: `${dotPct}%`,
                          ...(it.benchmarkSource === 'history' ? { borderColor: color } : { background: color }),
                        }}
                      />
                      {clampedHigh ? (
                        <span className="fg-lolli-chevron" style={{ left: `calc(${dotPct}% + 10px)`, color }}>
                          ›
                        </span>
                      ) : null}
                      {clampedLow ? (
                        <span className="fg-lolli-chevron" style={{ left: `calc(${dotPct}% - 18px)`, color }}>
                          ‹
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </span>
                <span className="fg-lolli-company" style={{ color }}>
                  {it.display || it.companyDisplay || '—'}
                </span>
                {onItemHide ? (
                  <button
                    type="button"
                    className="fg-lolli-hide"
                    aria-label={`Hide ${it.label} from this chart`}
                    title={`Hide ${it.label}`}
                    onClick={handleHide}
                  >
                    ✕
                  </button>
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="fg-lolli-grid fg-lolli-foot" aria-hidden="true">
        <span />
        <span />
        <span />
        <span className="fg-lolli-axis">
          <span style={{ left: 0 }}>below</span>
          <span
            style={{
              left: `${Math.min(88, Math.max(12, guidePct))}%`,
              transform: 'translateX(-50%)',
            }}
          >
            at benchmark
          </span>
          <span style={{ right: 0 }}>above</span>
        </span>
        <span />
        <span />
      </div>
    </div>
  )
}

export default LollipopBenchmark
